/**
 * Article-specific database operations
 */

import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
import { getQuizWithQuestions } from "./quiz.js";
import { getFlashcardDeckWithCards } from "./flashcard.js";
import { getBookmark } from "./bookmark.js";

/**
 * Save articles from offline data
 * @param {EduQuizDB} db - Database instance
 * @param {Array} resources - Resources array
 * @param {string} ARTICLE - Resource type constant
 */
export async function saveArticles(db, resources, ARTICLE) {
    const articlesData = [];
    const articleResourcesData = [];

    for (const resource of resources) {
        if (resource.type === ARTICLE) {
            // Save article
            articlesData.push({
                serverResourceId: resource.id,
                resourceId: resource.resource_id || resource.id,
                content: resource.content || "",
                name: resource.name,
                description: resource.description || null,
                level: resource.level || null,
                subject: resource.subject || null,
            });

            // Save article's attached resources
            if (
                resource.attached_resources &&
                resource.attached_resources.length > 0
            ) {
                resource.attached_resources.forEach((attachedResource) => {
                    articleResourcesData.push({
                        articleServerResourceId: resource.id,
                        serverResourceId: attachedResource.id,
                        resourceType: attachedResource.resource_type,
                        order: attachedResource.order,
                    });
                });
            }
        }
    }

    if (articlesData.length > 0) {
        console.log("Saving articles:", articlesData);
        await db.articles.bulkAdd(articlesData);
    }

    if (articleResourcesData.length > 0) {
        console.log("Saving article resources:", articleResourcesData);
        await db.articleResources.bulkAdd(articleResourcesData);
    }
}

/**
 * Save user's own articles from my_resources
 * @param {EduQuizDB} db - Database instance
 * @param {Array} myResources - My resources array
 * @param {string} ARTICLE - Resource type constant
 */
export async function saveMyArticles(db, myResources, ARTICLE) {
    for (const resource of myResources) {
        if (resource.resource_type === ARTICLE) {
            // Check if article already exists (to avoid duplicates)
            const existingArticle = await db.articles
                .where("serverResourceId")
                .equals(resource.id)
                .first();

            if (!existingArticle) {
                console.log("Saving user article:", resource);
                await db.articles.add({
                    serverResourceId: resource.id,
                    resourceId: resource.resource_id || resource.id,
                    content: resource.content || "",
                    name: resource.name,
                    description: resource.description || null,
                    level: resource.level || null,
                    subject: resource.subject || null,
                });

                // Save article's attached resources
                if (
                    resource.attached_resources &&
                    resource.attached_resources.length > 0
                ) {
                    for (const attachedResource of resource.attached_resources) {
                        await db.articleResources.add({
                            articleServerResourceId: resource.id,
                            serverResourceId: attachedResource.id,
                            resourceType: attachedResource.resource_type,
                            order: attachedResource.order,
                        });
                    }
                }
            }
        }
    }
}

/**
 * Get an article by server resource ID
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getArticle(db, serverResourceId) {
    try {
        return await db.articles
            .where("serverResourceId")
            .equals(serverResourceId)
            .first();
    } catch (error) {
        console.error("Failed to get article:", error);
        throw error;
    }
}

/**
 * Get attached resources for a specific article
 * @param {EduQuizDB} db - Database instance
 * @param {number} articleServerResourceId - Article server resource ID
 */
export async function getArticleResources(db, articleServerResourceId) {
    try {
        const articleResources = await db.articleResources
            .where("articleServerResourceId")
            .equals(articleServerResourceId)
            .toArray();

        // Sort by order
        return articleResources.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
        console.error("Failed to get article resources:", error);
        throw error;
    }
}

/**
 * Get an article with its attached resources
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getArticleWithAttachedResources(db, serverResourceId) {
    try {
        const article = await getArticle(db, serverResourceId);
        if (!article) {
            return null;
        }

        const attachedResourcesLinks = await getArticleResources(
            db,
            serverResourceId
        );

        // Load the actual resource data for each attached resource
        const attachedResources = await Promise.all(
            attachedResourcesLinks.map(async (link) => {
                let resourceData = null;

                // Use the resourceType to directly query the correct table
                switch (link.resourceType) {
                    case RESOURCE_TYPES.QUIZ:
                        resourceData = await getQuizWithQuestions(
                            db,
                            link.serverResourceId
                        );
                        break;
                    case RESOURCE_TYPES.FLASHCARD_DECK:
                        resourceData = await getFlashcardDeckWithCards(
                            db,
                            link.serverResourceId
                        );
                        break;
                    case RESOURCE_TYPES.BOOKMARK:
                        resourceData = await getBookmark(
                            db,
                            link.serverResourceId
                        );
                        break;
                    case RESOURCE_TYPES.ARTICLE:
                        // Don't recurse to avoid infinite loops
                        resourceData = await getArticle(db, link.serverResourceId);
                        break;
                    default:
                        console.warn(
                            `Unknown resource type: ${link.resourceType}`
                        );
                        return null;
                }

                return resourceData
                    ? {
                          ...resourceData,
                          type: link.resourceType,
                          order: link.order,
                      }
                    : null;
            })
        );

        return {
            ...article,
            attachedResources: attachedResources.filter((r) => r !== null),
        };
    } catch (error) {
        console.error("Failed to get article with attached resources:", error);
        throw error;
    }
}
